import { useCallback, useEffect, useMemo } from 'react';
import { Attributes, ItemCard } from '@leverege/ui-attributes';
import { TitleBar } from '@leverege/ui-plugin';
import { Flex, Pane, Text } from '@leverege/ui-elements';
import { Duration } from 'luxon';
import useResourceDataset from "../../hooks/useResourceDataset.js";
import useTimelineContext from "../../hooks/useTimelineContext.js";
import RsrcThumbnailRendererFactory from "../../../../components/RsrcThumbnailRendererFactory.js";
import S from './ResourceTimelineViewer.css';
import TimelineModel from "../../TimelineModel.js";
import TimelineUtil from "../../TimelineUtil.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_CONTEXT = {};
export default function ResourceTimelineViewer(props) {
  const {
    path,
    settings
  } = props;
  const {
    nearestData
  } = useTimelineContext();
  useEffect(() => {}, [nearestData, path, settings]);
  useResourceDataset({
    path
  });
  const {
    item,
    status
  } = useMemo(() => {
    return {
      item: nearestData[path.name]?.data,
      status: nearestData[path.name]?.status
    };
  }, [nearestData, path.name]);
  const url = useMemo(() => {
    return Attributes.get('rsrc.url', item);
  }, [item?.id]);
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "timelineItem",
    className: S.timelineItem,
    children: [/*#__PURE__*/_jsx(Pane, {
      variant: "timelineItemHeader",
      layout: "flex:rowSpaceBetweenCenter",
      children: /*#__PURE__*/_jsx(TitleBar, {
        title: path.name,
        titleVariant: "sectionTitle"
      })
    }), /*#__PURE__*/_jsxs(Pane, {
      className: S.timelineItemContent,
      variant: "timelineItemContent",
      children: [/*#__PURE__*/_jsx(ResourceImage, {
        item: item,
        loading: false,
        settings: settings,
        status: status,
        url: url
      }), /*#__PURE__*/_jsx(Pane, {
        className: S.details,
        children: /*#__PURE__*/_jsx(ItemCard, {
          model: settings?.details,
          context: item ? {
            id: item.id,
            target: [item]
          } : EMPTY_CONTEXT
        })
      })]
    })]
  });
}
function ResourceImage({
  item,
  settings,
  status,
  url
}) {
  const {
    selectionKey,
    rsrcDelegate,
    onThumbnailSelection
  } = useTimelineContext();
  const context = useMemo(() => {
    return {
      client: 'ResourceTimelineViewer',
      clientProps: {
        selectionKey,
        delegate: rsrcDelegate
      }
    };
  }, [selectionKey, rsrcDelegate]);
  const boundOnSelect = useCallback(() => {
    onThumbnailSelection({
      data: item
    });
  }, [onThumbnailSelection, item]);
  const precision = TimelineModel.getPrecision(settings);
  const precisionLabel = useMemo(() => {
    return Duration.fromDurationLike(TimelineUtil.convertPrecision(precision)).toHuman();
  }, [precision]);
  return /*#__PURE__*/_jsxs("div", {
    onClick: boundOnSelect,
    className: S.image,
    "data-has-image": !!url,
    style: {
      backgroundColor: url ? 'black' : 'white',
      cursor: url ? 'pointer' : 'default'
    },
    children: [status === 'loading' && /*#__PURE__*/_jsx("div", {
      className: S.loading,
      children: /*#__PURE__*/_jsx(Text, {
        variant: "loadingSpinnerLargeLight"
      })
    }), url && RsrcThumbnailRendererFactory.create(settings?.thumbnail, {
      item,
      src: url,
      loading: status === 'loading',
      context,
      key: url
    }), (status == null || status === 'done' && !url) && /*#__PURE__*/_jsxs(Flex, {
      variant: "colMCenter",
      className: S.noImage,
      children: [/*#__PURE__*/_jsxs(Text, {
        variant: "rsrcTimelineMessage",
        children: ["No image within ", precisionLabel, " of this timeframe."]
      }), /*#__PURE__*/_jsx(Text, {
        variant: "caption",
        children: "Choose a new time to view nearby images."
      })]
    }), status === 'error' && /*#__PURE__*/_jsx(Text, {
      variant: "rsrcTimelineError|videoControlsError",
      children: "An unknown error occurred."
    })]
  });
}